import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import apiClient from '../utils/apiClient.js';
import { useLinkCopiedToast } from '../hooks/useLinkCopiedToast';

function roleLabel(role, t) {
  if (role === 'Tea') return t('report.teacher');
  if (role === 'Sta') return t('report.staff');
  return t('report.student');
}

function JoinSchool() {
  const { code } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { isAuthenticated, refreshUserProfile } = useAuth();
  const [invite, setInvite] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [accepting, setAccepting] = useState(false);
  const { copyInviteLink, linkCopiedToast } = useLinkCopiedToast();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError('');
      try {
        const res = await fetch(`/api/invites/${encodeURIComponent(code)}/preview`);
        const data = await res.json();
        if (!res.ok) throw new Error(data.message || 'Invite not found');
        if (!cancelled) setInvite(data);
      } catch (e) {
        if (!cancelled) setError(e.message || t('school.inviteInvalid'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [code, t]);

  const joinUrl = invite?.join_url || `${window.location.origin}/join/${code}`;

  const handleCopy = () => copyInviteLink(joinUrl);

  const handleContinue = async () => {
    if (!isAuthenticated) {
      navigate(`/register?invite=${encodeURIComponent(code)}`);
      return;
    }

    setAccepting(true);
    setError('');
    try {
      const res = await apiClient.post('/api/affiliations/accept', {
        invite_code: code
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || t('school.acceptInviteFailed'));
      await refreshUserProfile?.();
      navigate('/view-report');
    } catch (e) {
      setError(e.message || t('school.acceptInviteFailed'));
    } finally {
      setAccepting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-[50vh] flex items-center justify-center bg-canvas px-4">
        <p className="text-ink-muted">{t('common.loading')}</p>
      </div>
    );
  }

  if (error || !invite) {
    return (
      <div className="min-h-[50vh] flex items-center justify-center bg-canvas px-4">
        <div className="max-w-md w-full bg-surface shadow rounded-lg p-8 text-center">
          <h1 className="text-xl font-semibold text-ink">{t('school.inviteInvalidTitle')}</h1>
          <p className="mt-2 text-sm text-ink-muted">{error || t('school.inviteInvalid')}</p>
          <Link to="/" className="mt-6 inline-block text-link hover:text-link-hover font-medium">
            {t('navigation.home')}
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[50vh] bg-gradient-to-b from-blue-50 to-gray-50 py-10 px-4 sm:px-6">
      <div className="max-w-lg mx-auto">
        <div className="bg-surface shadow-lg rounded-2xl overflow-hidden border border-edge">
          <div className="bg-[#29496a] px-6 py-8 text-white text-center">
            <p className="text-sm uppercase tracking-wider text-blue-100">{t('school.inviteBadge')}</p>
            <h1 className="mt-2 text-2xl font-bold">{invite.school.name}</h1>
            <p className="mt-1 text-blue-100 text-sm">{t(`common.districts.${invite.school.district}`, { defaultValue: invite.school.district })}</p>
          </div>

          <div className="px-6 py-8 space-y-6">
            {invite.label ? (
              <p className="text-center text-ink-muted font-medium">{invite.label}</p>
            ) : null}

            <div className="rounded-lg border border-edge bg-canvas px-4 py-3 text-center">
              <p className="text-xs font-medium text-ink-subtle uppercase tracking-wide">{t('school.inviteCode')}</p>
              <p className="mt-1 text-2xl font-mono font-bold tracking-widest text-[#29496a]">{invite.code}</p>
            </div>

            <div>
              <p className="text-sm text-ink-muted text-center">{t('school.inviteForRole', { role: roleLabel(invite.intended_role, t) })}</p>
            </div>

            {error ? (
              <p className="text-sm text-red-600 text-center" role="alert">{error}</p>
            ) : null}

            <button
              type="button"
              onClick={handleContinue}
              disabled={accepting}
              className="w-full py-3 px-4 rounded-lg bg-primary hover:bg-primary-hover text-primary-foreground font-semibold shadow-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {accepting
                ? t('common.loading')
                : (isAuthenticated ? t('school.acceptInvite') : t('school.registerWithInvite'))}
            </button>

            <div className="border-t border-edge pt-4">
              <p className="text-xs text-ink-subtle text-center mb-2">{t('school.shareLink')}</p>
              <div className="flex gap-2">
                <input
                  readOnly
                  value={joinUrl}
                  className="flex-1 text-xs border border-edge rounded-md px-3 py-2 bg-surface text-ink-muted"
                />
                <button
                  type="button"
                  onClick={handleCopy}
                  className="shrink-0 px-3 py-2 text-sm font-medium rounded-md border border-edge-strong hover:bg-surface-muted"
                >
                  {t('school.copy')}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      {linkCopiedToast}
    </div>
  );
}

export default JoinSchool;
